import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Row, Col, message, Skeleton, Divider, Table, Tag, Button, Card, Space } from "antd";
import {
  UserOutlined,
  PhoneOutlined,
  MobileOutlined,
  MailOutlined,
  IdcardOutlined,
  AuditOutlined,
  EditOutlined,
  GlobalOutlined,
  CheckCircleFilled,
  WarningFilled,
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import MainTitle from "../verifications/MainTitle";
import Subtitle from "../verifications/Subtitle";
import DataGroup from "../verifications/DataGroup";
import DataItem from "../verifications/DataItem";
import AnimateItems from "../../utils/AnimateItems";
import PersonaForm from "../verifications/forms/persona/PersonaForm";

export default function PersonPreview() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editVisible, setEditVisible] = useState(false);

  const fetchData = () => {
    setLoading(true);
    axios
      .get(`${process.env.REACT_APP_API_URL}/personas/details/${id}`)
      .then((res) => {
        setData(res.data);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        message.error("Error al obtener detalles de la persona: " + err.message);
      });
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const roleMapping = {
    PV: { label: "Recibe la verificación", color: "geekblue" },
    PS: { label: "Solicita la verificación", color: "magenta" },
    PF: { label: "Firma el contrato", color: "purple" },
    PA: { label: "Atiende la visita", color: "cyan" },
    Testigo: { label: "Testigo", color: "orange" },
  };

  const participationColumns = [
    {
      title: "Folio",
      dataIndex: "ControlInterno",
      key: "ControlInterno",
      width: 160,
      fixed: "left",
      render: (text, record) => (
        <Link to={`/verificaciones/${record.VerificacionID}`}>
          {record.FolioAmuvie ? (
            <CheckCircleFilled style={{ color: "#52c41a", marginRight: "10px" }} />
          ) : (
            <WarningFilled style={{ color: "orange", marginRight: "10px" }} />
          )}
          {text}
        </Link>
      ),
    },
    {
      title: "Roles",
      dataIndex: "Roles",
      key: "Roles",
      render: (text) => (
        <Space wrap>
          {(text || "").split(", ").map((role) => (
            <Tag key={role} color={roleMapping[role]?.color || "blue"}>
              {roleMapping[role]?.label || role}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: "Fecha",
      dataIndex: "FechaCreacion",
      key: "FechaCreacion",
      width: 120,
      render: (text) => (text ? moment.utc(text).format("DD/MM/YYYY") : "N/D"),
    },
    {
      title: "Descripción",
      dataIndex: "DescripcionBreve",
      key: "DescripcionBreve",
      width: 200,
      render: (text) => <span className="break-by-word">{text || "N/D"}</span>,
    },
    {
      title: "Cliente",
      dataIndex: "NombreCliente",
      key: "NombreCliente",
      render: (text) => <span className="break-by-word">{text || "N/D"}</span>,
    },
    {
      title: "Razón Social",
      dataIndex: "NombreCompleto",
      key: "NombreCompleto",
      render: (text) => <span className="break-by-word">{text || "N/D"}</span>,
    },
    {
      title: "Tensión (V)",
      dataIndex: "TensionElectricaSuministro",
      key: "TensionElectricaSuministro",
      align: "right",
      render: (text) => (text ? text.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "N/D"),
    },
    {
      title: "Carga (kW)",
      dataIndex: "CargaInstalada",
      key: "CargaInstalada",
      align: "right",
      render: (text) => (text ? Number(text).toFixed(2) : "N/D"),
    },
    {
      title: "Monto",
      dataIndex: "Monto",
      key: "Monto",
      align: "right",
      fixed: "right",
      width: 110,
      render: (text) =>
        text
          ? "$" +
            Number(text)
              .toFixed(2)
              .toString()
              .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
          : "N/D",
    },
  ];

  const persona = data?.persona;
  const telefonos = data?.telefonos || [];
  const correos = data?.correos || [];
  const datos = data?.datos || [];
  const participaciones = data?.participaciones || [];

  return (
    <>
      <Row align="middle">
        <Col span={18}>
          {loading ? (
            <Skeleton.Input active style={{ width: 300 }} />
          ) : (
            <div>
              <MainTitle 
                title={
                  <Space>
                    <UserOutlined />
                    {persona?.NombreCompleto}
                  </Space>
                } 
                marginBottom={10} 
              />
              <div style={{ fontSize: '14px', color: '#666', marginTop: '-5px' }}>
                Persona {persona?.TipoPersona} | {persona?.Nacionalidad}
              </div>
            </div>
          )}
        </Col>
        <Col span={6} style={{ textAlign: "right" }}>
          <Button 
            type="primary" 
            icon={<EditOutlined />} 
            onClick={() => setEditVisible(true)}
            disabled={loading}
          >
            Editar Persona
          </Button>
        </Col>
      </Row>
      <Divider style={{ margin: '15px 0' }} />

      <AnimateItems delay={0.15}>
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <DataGroup title="Información de Contacto" icon={<PhoneOutlined />}>
              <Subtitle title="Teléfonos" marginTop={0} />
              {telefonos.length > 0 ? (
                telefonos.map((t) => (
                  <DataItem
                    key={t.TelefonoID}
                    icon={t.TipoTelefono === "Móvil" ? <MobileOutlined /> : <PhoneOutlined />}
                    keyName={t.TipoTelefono}
                    value={t.Telefono}
                    loading={loading}
                    fieldWidth={12}
                  />
                ))
              ) : (
                <div style={{ padding: '8px', color: '#ccc' }}>Sin teléfonos registrados</div>
              )}

              <Subtitle title="Correos Electrónicos" marginTop={16} />
              {correos.length > 0 ? (
                correos.map((c) => (
                  <DataItem
                    key={c.CorreoElectronicoID}
                    icon={<MailOutlined />}
                    keyName="Email"
                    value={c.CorreoElectronico}
                    loading={loading}
                    fieldWidth={12}
                  />
                ))
              ) : (
                <div style={{ padding: '8px', color: '#ccc' }}>Sin correos registrados</div>
              )}
            </DataGroup>
          </Col>

          <Col xs={24} lg={12}>
            <DataGroup title="Identificaciones y Otros" icon={<IdcardOutlined />}>
              {datos.length > 0 ? (
                datos.map((d) => (
                  <DataItem
                    key={d.DatoID}
                    icon={<IdcardOutlined />}
                    keyName={d.TipoDato_Corto}
                    value={d.Dato}
                    loading={loading}
                    fieldWidth={12}
                  />
                ))
              ) : (
                <div style={{ padding: '8px', color: '#ccc' }}>Sin identificaciones registradas</div>
              )}
              
              <Subtitle title="Datos de Sistema" marginTop={16} />
              <DataItem
                icon={<GlobalOutlined />}
                keyName="Nacionalidad"
                value={persona?.Nacionalidad}
                loading={loading}
                fieldWidth={12}
              />
              <DataItem
                icon={<UserOutlined />}
                keyName="Tipo"
                value={persona?.TipoPersona}
                loading={loading}
                fieldWidth={12}
              />
            </DataGroup>
          </Col>
        </Row>

        <Row style={{ marginTop: 24 }}>
          <Col span={24}>
            <Card 
              title={
                <Space>
                  <AuditOutlined />
                  <span>Participación en Verificaciones</span>
                </Space>
              }
              style={{ borderRadius: "12px", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}
            >
              <Table
                dataSource={participaciones}
                columns={participationColumns}
                rowKey="VerificacionID"
                pagination={{ 
                  defaultPageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ["5", "10", "20", "50"]
                }}
                loading={loading}
                scroll={{ x: "max-content" }}
              />
            </Card>
          </Col>
        </Row>
      </AnimateItems>

      {persona && (
        <PersonaForm
          personaFormVisible={editVisible}
          setPersonaFormVisible={setEditVisible}
          personaEditing={id}
          setPersonasList={fetchData} 
          setDatosPersona={() => {}}
          setTelefonosFijos={() => {}}
          setTelefonosMoviles={() => {}}
          setCorreosPersona={() => {}}
        />
      )}
    </>
  );
}
